import type { KkmJournalDeviceKind, KkmJournalEvent, KkmJournalPhase, KkmJournalSink } from '@/shared/api';
import type { OrderFiscalCommandLogEntryInput } from '@shared/api/graphql/generated';

import { graphqlKkmCommandStatus, useFiscalWorkstationStorage } from '@/entities/fiscalization';
import { isRecord } from '@/shared/api';
import { useLocationStorage, useOrganizationStorage } from '@/shared/config';
import { OrderFiscalCommandDeviceKind, OrderFiscalCommandPhase } from '@shared/api/graphql/generated';

import { beaconKkmJournalEntries, recordKkmJournalEntries } from '../api/kkm-journal-api';
import { createKkmJournalBuffer, type KkmJournalBufferedEntry } from './kkm-journal-buffer';
import { createKkmJournalNoiseFilter } from './kkm-journal-noise-filter';

export interface KkmJournalRuntimeSink extends KkmJournalSink {
  flushWithBeacon(): void;
}

export function createKkmJournalSink(): KkmJournalRuntimeSink {
  const { getLocationId } = useLocationStorage();
  const { getOrganizationId } = useOrganizationStorage();
  const pageSessionId = createId();
  const noiseFilter = createKkmJournalNoiseFilter();
  let kkmserverVersion: string | null = null;

  const buffer = createKkmJournalBuffer({
    send: async (batch) => {
      const scope = batchScope(batch);
      await recordKkmJournalEntries(
        batch.map(({ entry }) => entry),
        scope.organizationId,
        scope.locationId,
      );
    },
    sendBeacon: (batch) => {
      const scope = batchScope(batch);

      return beaconKkmJournalEntries(
        batch.map(({ entry }) => entry),
        scope.organizationId,
        scope.locationId,
      );
    },
  });

  return {
    flushWithBeacon: () => buffer.flushWithBeacon(),
    record(event): void {
      try {
        const organizationId = getOrganizationId();
        const locationId = event.locationId ?? getLocationId();
        const clientEventId = event.clientEventId ?? createId();

        if (organizationId === null || locationId === null || clientEventId === null || pageSessionId === null) {
          return;
        }

        kkmserverVersion = extractKkmserverVersion(event) ?? event.kkmserverVersion ?? kkmserverVersion;

        // Фильтр читает только исходные поля события, поэтому отсекаем шум до обогащения.
        if (!noiseFilter.shouldRecord(event)) {
          return;
        }

        const workstationStorage = useFiscalWorkstationStorage(organizationId, locationId);

        const pendingOperationIds = workstationStorage.pendingOperationIds();
        const enrichedEvent: KkmJournalEvent = {
          ...event,
          browserWorkstationId: event.browserWorkstationId ?? workstationStorage.state.value.browserWorkstationId,
          clientEventId,
          fiscalOperationId: event.fiscalOperationId ?? fiscalOperationId(event.idCommand, pendingOperationIds),
          kkmserverVersion,
          locationId,
          pageSessionId: event.pageSessionId ?? pageSessionId,
          userAgent: event.userAgent ?? navigator.userAgent,
        };

        buffer.record({
          entry: toGraphqlEntry(enrichedEvent),
          locationId,
          organizationId,
        });
      } catch {
        // Диагностический sink не влияет на кассовую операцию.
      }
    },
  };
}

function toGraphqlEntry(event: KkmJournalEvent): OrderFiscalCommandLogEntryInput {
  return {
    attempt: event.attempt,
    browserWorkstationId: event.browserWorkstationId ?? null,
    clientAt: event.clientAt,
    clientEventId: requireValue(event.clientEventId),
    command: event.command,
    deviceKind: graphqlDeviceKind(event.deviceKind),
    durationMs: event.durationMs,
    errorCode: event.errorCode,
    fiscalOperationId: event.fiscalOperationId ?? null,
    idCommand: event.idCommand,
    idDevice: event.idDevice,
    kkmserverVersion: event.kkmserverVersion ?? null,
    locationId: requireValue(event.locationId),
    pageSessionId: requireValue(event.pageSessionId),
    payload: event.payload,
    phase: graphqlPhase(event.phase),
    refundId: event.refundId ?? null,
    orderId: event.orderId ?? null,
    shiftId: event.shiftId ?? null,
    status: graphqlKkmCommandStatus(event.status),
    userAgent: event.userAgent ?? null,
    vendorMessage: event.vendorMessage,
    workstationId: event.workstationId ?? null,
  };
}

function graphqlPhase(phase: KkmJournalPhase): OrderFiscalCommandPhase {
  const phases: Record<KkmJournalPhase, OrderFiscalCommandPhase> = {
    request: OrderFiscalCommandPhase.Request,
    response: OrderFiscalCommandPhase.Response,
    threw: OrderFiscalCommandPhase.Threw,
    timeout: OrderFiscalCommandPhase.Timeout,
  };

  return phases[phase];
}

function graphqlDeviceKind(deviceKind: KkmJournalDeviceKind): OrderFiscalCommandDeviceKind {
  const kinds: Record<KkmJournalDeviceKind, OrderFiscalCommandDeviceKind> = {
    acquiring: OrderFiscalCommandDeviceKind.Acquiring,
    kkt: OrderFiscalCommandDeviceKind.Kkt,
    server: OrderFiscalCommandDeviceKind.Server,
  };

  return kinds[deviceKind];
}

function fiscalOperationId(idCommand: string, pendingOperationIds: readonly string[]): string | null {
  const candidate = idCommand.endsWith('-b') ? idCommand.slice(0, -2) : idCommand;

  return pendingOperationIds.includes(candidate) ? candidate : null;
}

function extractKkmserverVersion(event: KkmJournalEvent): string | null {
  if (event.command !== 'GetServerData' || event.phase !== 'response' || !isRecord(event.payload)) {
    return null;
  }

  const serverData = event.payload.ServerData;

  return isRecord(serverData) && typeof serverData.ServerVersion === 'string' ? serverData.ServerVersion : null;
}

function batchScope(batch: readonly KkmJournalBufferedEntry[]): { locationId: string; organizationId: string } {
  const first = batch[0];

  if (first === undefined) {
    throw new Error('KKM journal batch has no tenant scope');
  }

  return {
    locationId: first.locationId,
    organizationId: first.organizationId,
  };
}

function createId(): string | null {
  return globalThis.crypto?.randomUUID() ?? null;
}

function requireValue(value: string | undefined): string {
  if (value === undefined) {
    throw new Error('KKM journal event is missing runtime context');
  }

  return value;
}
